/**
 * Solution details panel — verbatim visual port from the standalone app.
 *
 * Recipe HTML rendered through DOMPurify for safety.
 * Includes dose calculator and print button.
 */
import { useState, useMemo, useEffect, type ReactNode } from 'react';
import DOMPurify from 'dompurify';

import type { Solution, SolutionsCatalog } from '@reefapp/shared';

import { CalibrationHelper } from './CalibrationHelper';
import { DoseCalculator } from './DoseCalculator';
import { CompareView } from './CompareView';
import { renderRecipeHtml, volumeLabel } from './recipeRender';
import { PrintIcon } from './icons';

import { StarButton } from './StarButton';
import { useStockSolutionsFavorites } from './useStockSolutionsFavorites';
import type { PrefillFromDeepLink } from './types';
import './print.css';

interface SolutionDetailsProps {
  solution: Solution;
  solutions: SolutionsCatalog;
  prefill?: PrefillFromDeepLink;
}

interface InfoBlockProps {
  title: string;
  children: ReactNode;
}

function InfoBlock({ title, children }: InfoBlockProps) {
  return (
    <div>
      <h4 className="text-lg font-semibold text-slate-300 border-l-4 border-cyan-400 pl-3 mb-2">
        {title}
      </h4>
      <p className="text-slate-300 text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export function SolutionDetails({ solution, solutions, prefill }: SolutionDetailsProps) {
  const { isFavorite, toggleFavorite } = useStockSolutionsFavorites();
  const [compareOpen, setCompareOpen] = useState(false);
  const defaultVolume: string =
    solution.recipe.amounts ? Object.keys(solution.recipe.amounts)[0] ?? '' : '';
  const [selectedVolume, setSelectedVolume] = useState(defaultVolume);

  const hasCalculator =
    solution.kind === 'parameter' || solution.kind === 'ratio';

  useEffect(() => {
    const amounts = solution.recipe.amounts;
    const availableVolumes = amounts ? Object.keys(amounts) : [];
    const firstVolume = availableVolumes[0];
    if (firstVolume !== undefined && selectedVolume !== '' && !availableVolumes.includes(selectedVolume)) {
      setSelectedVolume(firstVolume);
    } else if (availableVolumes.length === 0) {
      setSelectedVolume('');
    }
  }, [solution, selectedVolume]);

  const recipeHtml = useMemo(
    () => renderRecipeHtml(solution.recipe, selectedVolume),
    [solution, selectedVolume],
  );


  const sanitisedRecipe = DOMPurify.sanitize(recipeHtml);

  return (
    <div className="solution-details-card bg-slate-800 p-6 sm:p-8 rounded-2xl border border-slate-700 shadow-lg animate-fade-in">
      <div className="flex justify-between items-start mb-2">
        <h2 className="text-3xl font-bold text-cyan-300">{solution.name}</h2>
        <div className="flex items-center gap-2 print:hidden">
          <StarButton
            slug={solution.slug}
            favorited={isFavorite(solution.slug)}
            onToggle={() => toggleFavorite(solution.slug)}
          />
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Compare
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            <PrintIcon className="h-5 w-5" />
            Print
          </button>
        </div>
      </div>

      {solution.safety && (
        <div
          className="my-4 p-4 rounded-lg bg-red-900 border border-red-500 text-red-300 text-sm"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(solution.safety) }}
        />
      )}

      {solution.notes && (
        <div
          className="my-4 p-4 rounded-lg bg-amber-900 border border-amber-500/50 text-amber-300 text-sm"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(solution.notes) }}
        />
      )}

      <div className="space-y-6 mt-6">
        <InfoBlock title="What it is">{solution.what_it_is}</InfoBlock>

        <InfoBlock title="What it does">{solution.what_it_does}</InfoBlock>

        <div>
          <h4 className="text-lg font-semibold text-slate-300 border-l-4 border-cyan-400 pl-3 mb-2">
            Stock Solution Recipe
          </h4>
          {solution.recipe.amounts && (
            <div className="volume-select-section mb-4">
              <label
                htmlFor="volume-select"
                className="block text-sm font-medium text-slate-300 mb-2"
              >
                Select final volume of stock solution:
              </label>
              <select
                id="volume-select"
                value={selectedVolume}
                onChange={(e) => setSelectedVolume(e.target.value)}
                className="w-full sm:w-1/2 bg-slate-700 border border-slate-600 rounded-lg py-2 px-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
              >
                {Object.entries(solution.recipe.amounts).map(([vol]) => (
                  <option key={vol} value={vol}>
                    {volumeLabel(vol)}
                  </option>
                ))}

              </select>
            </div>
          )}
          <div
            className="recipe-block p-4 rounded-lg bg-slate-900 text-slate-300 text-sm space-y-3"
            dangerouslySetInnerHTML={{ __html: sanitisedRecipe }}
          />
        </div>

        <InfoBlock title="Dosing Effect">
          <span className="font-mono text-cyan-300">{solution.dosing_effect}</span>
        </InfoBlock>

        {solution.ingredients && (
          <InfoBlock title="Ingredients">{solution.ingredients}</InfoBlock>
        )}

        {solution.equipment && (
          <InfoBlock title="Equipment">{solution.equipment}</InfoBlock>
        )}

        {solution.instructions && solution.instructions.length > 0 && (
          <div>
            <h4 className="text-lg font-semibold text-slate-300 border-l-4 border-cyan-400 pl-3 mb-2">
              Instructions
            </h4>
            <ol className="list-decimal list-inside text-slate-300 text-sm leading-relaxed space-y-1">
              {solution.instructions.map((step, i) => (
                <li key={`step-${i}`}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {solution.storage && (
          <InfoBlock title="Storage">{solution.storage}</InfoBlock>
        )}

        {solution.usageTips && (
          <InfoBlock title="Usage Tips">{solution.usageTips}</InfoBlock>
        )}


        {hasCalculator && (
          <div className="dose-calculator-print-section">
          <DoseCalculator solution={solution} prefill={prefill} />
          </div>
        )}

        <div className="print:hidden">
          <CalibrationHelper slug={solution.slug} />
        </div>
      </div>

      <CompareView
        open={compareOpen}
        primary={solution}
        solutions={solutions}
        onClose={() => setCompareOpen(false)}
      />
    </div>
  );
}
